/**
 * My Reservations Page (Paiseh A.dc design refresh)
 * Shows items the user has reserved on other wishlists
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useAdSense } from '@/hooks/useAdSense'
import { LoginButton } from '@/components/auth/LoginButton'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { useReservations } from '@/hooks/useReservations'
import { unreserveItem } from '@/services/reservation.service'
import { analyticsEvents } from '@/utils/analytics'
import { Reservation } from '@/types'
import { getOccasionTheme, SWATCH_AVAIL } from '@/utils/occasionTheme'
import toast from 'react-hot-toast'

export const MyReservations = () => {
  const { user } = useAuth()
  const { reservations, reservationsByWishlist, loading, error } = useReservations()
  const [reservationToCancel, setReservationToCancel] = useState<Reservation | null>(null)
  const [isUnreserving, setIsUnreserving] = useState(false)

  // Load AdSense script for this page
  useAdSense()

  // Track reservations page view
  useEffect(() => {
    if (user && !loading) {
      analyticsEvents.reservationsViewed()
    }
  }, [user, loading])

  const handleUnreserve = async () => {
    if (!reservationToCancel) return

    setIsUnreserving(true)
    try {
      await unreserveItem(reservationToCancel.wishlistId, reservationToCancel.itemId)
      analyticsEvents.itemUnreserved(reservationToCancel.wishlistId, reservationToCancel.itemId)
      toast.success('Reservation cancelled')
      setReservationToCancel(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel reservation')
    } finally {
      setIsUnreserving(false)
    }
  }

  const formatPrice = (price: number | null, currency: string) => {
    if (!price) return null
    const formatter = new Intl.NumberFormat('en-SG', { style: 'currency', currency: currency || 'SGD' })
    return formatter.format(price / 100)
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return ''
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
    return date.toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  // If not authenticated, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center font-jakarta">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-24 h-24 bg-paiseh-oliveTint rounded-full flex items-center justify-center mx-auto mb-6 text-5xl">
            🎁
          </div>
          <h2 className="font-display font-extrabold text-3xl text-ink mb-4">My Reservations</h2>
          <p className="text-paiseh-muted mb-8">Sign in to view your reserved items</p>
          <div className="flex justify-center">
            <LoginButton />
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream font-jakarta">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-paiseh-orange mx-auto mb-4"></div>
          <p className="text-paiseh-muted">Loading reservations...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center font-jakarta">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-24 h-24 bg-paiseh-peach rounded-full flex items-center justify-center mx-auto mb-6 text-5xl">
            😕
          </div>
          <h2 className="font-display font-extrabold text-3xl text-ink mb-4">Error Loading Reservations</h2>
          <p className="text-paiseh-muted mb-8">
            {error.message || 'Failed to load your reservations. Please try again later.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-paiseh-orange text-white rounded-full font-bold hover:opacity-95 transition-opacity"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const wishlistCount = Object.keys(reservationsByWishlist).length

  return (
    <div className="min-h-screen bg-cream text-ink font-jakarta">
      <main className="max-w-[1180px] mx-auto px-5 sm:px-10 py-14">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-[26px]">
          <div>
            <div className="text-[13px] font-bold text-paiseh-olive uppercase tracking-[0.06em]">
              Just for you 🤫
            </div>
            <h1 className="font-display font-extrabold text-[30px] sm:text-[34px] mt-1.5">
              My Reservations
            </h1>
            <p className="text-[15.5px] text-paiseh-muted mt-2 max-w-[440px]">
              Every gift you&rsquo;ve claimed, in one place. Only you can see this — the wishlist
              owner never finds out it&rsquo;s you.
            </p>
          </div>
          <div className="bg-white border border-ink/[.08] rounded-[16px] px-5 py-3 text-center">
            <div className="font-display font-extrabold text-2xl text-paiseh-orange">
              {reservations.length}
            </div>
            <div className="text-xs font-semibold text-[#8A7E72]">reserved</div>
          </div>
        </div>

        {reservations.length === 0 ? (
          <div className="bg-white rounded-[28px] border border-ink/[.06] shadow-[0_20px_50px_rgba(43,36,32,.08)] py-16 px-8 text-center">
            <div className="w-20 h-20 rounded-[24px] bg-paiseh-oliveTint text-paiseh-olive flex items-center justify-center text-4xl mx-auto mb-5">
              🎁
            </div>
            <h3 className="font-display font-extrabold text-[24px] mb-2">No reservations yet</h3>
            <p className="text-[16px] leading-[1.55] text-paiseh-muted max-w-[420px] mx-auto mb-7">
              When you reserve a gift from someone&rsquo;s shared wishlist, it lands here so you
              never double-buy or forget. Your picks stay a secret from the owner. 🤫
            </p>
            <Link
              to="/wishlists"
              className="inline-block bg-paiseh-orange text-white font-bold text-[15px] px-7 py-3.5 rounded-full shadow-[0_8px_18px_rgba(240,98,62,.3)] hover:opacity-95 transition-opacity"
            >
              Browse wishlists →
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-[28px] border border-ink/[.06] shadow-[0_20px_50px_rgba(43,36,32,.08)] overflow-hidden">
            {reservations.map((r) => {
              const theme = getOccasionTheme(r.wishlistTitle)
              return (
                <div
                  key={r.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 px-6 sm:px-7 py-5 border-b border-ink/[.06] last:border-b-0"
                >
                  <div
                    className="w-[60px] h-[60px] flex-none rounded-[14px]"
                    style={{ background: SWATCH_AVAIL }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[17px] truncate">{r.itemTitle}</div>
                    <div className="text-[13.5px] text-paiseh-faint mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>Reserved {formatDate(r.reservedAt)}</span>
                      {r.itemUrl && (
                        <a
                          href={r.itemUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-paiseh-orange font-semibold hover:underline"
                        >
                          View product ↗
                        </a>
                      )}
                    </div>
                    {r.note && (
                      <p className="text-[13px] text-paiseh-muted mt-1">
                        <strong className="font-semibold">Note:</strong> {r.note}
                      </p>
                    )}
                  </div>
                  <div className="sm:w-[210px] flex-none">
                    <Link
                      to={`/w/${r.wishlistId}`}
                      className="inline-flex items-center gap-2 bg-[#FCF2E7] rounded-full px-3 py-1.5 hover:bg-paiseh-peach transition-colors"
                    >
                      <span
                        className="w-[22px] h-[22px] rounded-full text-white flex items-center justify-center text-xs"
                        style={{ background: theme.circle }}
                      >
                        {theme.emoji}
                      </span>
                      <span className="text-[13px] font-semibold text-paiseh-muted truncate max-w-[150px]">
                        {r.wishlistTitle}
                      </span>
                    </Link>
                  </div>
                  <div className="sm:w-[70px] flex-none sm:text-right font-bold text-paiseh-orange">
                    {formatPrice(r.itemPrice, 'SGD')}
                  </div>
                  <div className="sm:w-[130px] flex-none sm:text-right">
                    <button
                      onClick={() => setReservationToCancel(r)}
                      className="inline-flex items-center gap-2 font-bold text-[13.5px] px-4 py-2.5 rounded-full bg-paiseh-peach text-paiseh-orangeDark hover:bg-[#F7CDB8] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )
            })}
            <div className="px-6 sm:px-7 py-5 flex items-center justify-between">
              <span className="text-sm text-[#8A7E72] font-semibold">
                Showing gifts across {wishlistCount} {wishlistCount === 1 ? 'wishlist' : 'wishlists'}
              </span>
              <Link to="/wishlists" className="text-sm font-bold text-paiseh-orange hover:underline">
                Browse more lists →
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Confirm Cancel Dialog */}
      <ConfirmDialog
        isOpen={!!reservationToCancel}
        onClose={() => setReservationToCancel(null)}
        onConfirm={handleUnreserve}
        title="Cancel Reservation"
        message={`Are you sure you want to cancel your reservation for "${reservationToCancel?.itemTitle}"?`}
        variant="warning"
        confirmText="Cancel Reservation"
        cancelText="Keep Reservation"
        isLoading={isUnreserving}
      />
    </div>
  )
}
